<template lang="html">
  <div class="cw-accept-account">
    <div class="card-image">
      <div class="hero is-small">
        <div class="hero-body has-text-centered">
          <div class="title is-3">Create An Account</div>
          <p class="is-size-4">
            Join <strong>{{ tokenPayload.company }}</strong>
          </p>
        </div>
      </div>
    </div>
    <div class="card-content">
      <cw-step-flow :steps="steps" :currentStep="currentStep" />
      <section class="section">
        <!-- Step 2: Account Details -->
        <div v-if="currentStep === 2">
          <div class="label font-light">Invited As</div>
          <p class="is-size-4 is-pad-bottom-1">{{ tokenPayload.email }}</p>

          <form class="form">
            <div class="field">
              <div class="label font-light">Information</div>
              <div class="control has-icons-left">
                <span class="icon is-left">
                  <i class="las la-2x la-male"></i
                ></span>
                <input
                  type="text"
                  class="input is-underlined is-medium"
                  :class="{ 'is-danger': $v.firstName.$error }"
                  @input="$v.firstName.$touch()"
                  @focus="$v.firstName.$touch()"
                  placeholder="Given Name"
                  v-model="firstName"
                />
              </div>

              <p
                class="help"
                v-if="
                  ($v.firstName.$dirty && !$v.firstName.required) ||
                    ($v.firstName.$dirty && !$v.firstName.minLength)
                "
              >
                Required, must be at least 3 characters long.
              </p>
            </div>
            <div class="field">
              <div class="control has-icons-left">
                <span class="icon is-left">
                  <i class="las la-2x la-male"></i
                ></span>
                <input
                  type="text"
                  class="input is-underlined is-medium"
                  :class="{ 'is-danger': $v.lastName.$error }"
                  @input="$v.lastName.$touch()"
                  @focus="$v.lastName.$touch()"
                  placeholder="Surname"
                  v-model="lastName"
                />
              </div>

              <p
                class="help"
                v-if="
                  ($v.lastName.$dirty && !$v.lastName.required) ||
                    ($v.lastName.$dirty && !$v.lastName.minLength)
                "
              >
                Required, must be at least 3 characters long.
              </p>
            </div>
            <div class="field">
              <div class="control has-icons-left">
                <span class="icon is-left">
                  <i class="las la-2x la-phone"></i>
                </span>
                <cleave
                  ref="phoneInput"
                  class="input is-underlined is-medium"
                  :class="{ 'is-danger': $v.phone.$error }"
                  type="tel"
                  :options="{ phone: true, phoneRegionCode: 'us' }"
                  placeholder="Personal Phone Number"
                  v-model="phone"
                  :events="{
                    onInput: $v.phone.$touch,
                    onFocus: $v.phone.$touch,
                  }"
                />
                <p class="help" v-if="$v.phone.dirty && !$v.phone.required">
                  This field is required.
                </p>
                <p
                  class="help"
                  v-if="!$v.phone.minLength || !$v.phone.maxLength"
                >
                  Phone number must be 10 digits including the area code
                </p>
              </div>
            </div>
            <div class="field">
              <div class="label font-light">Password</div>
              <div class="control has-icons-left">
                <span class="icon is-left">
                  <i class="las la-2x la-key"></i>
                </span>
                <input
                  type="password"
                  class="input is-underlined is-medium"
                  :class="{ 'is-danger': passwordError !== null }"
                  @input="$v.password.$touch()"
                  @focus="$v.password.$touch()"
                  placeholder="Password"
                  v-model="password"
                />
                <p class="help" v-if="passwordError">{{ passwordError }}</p>
              </div>
            </div>
            <div class="field">
              <div class="control has-icons-left">
                <span class="icon is-left">
                  <i class="las la-2x la-key"></i>
                </span>
                <input
                  type="password"
                  class="input is-underlined is-medium"
                  :class="{ 'is-danger': confirmPasswordError !== null }"
                  @input="$v.confirmPassword.$touch()"
                  @focus="$v.confirmPassword.$touch()"
                  placeholder="Confirm Password"
                  v-model="confirmPassword"
                />
                <p class="help" v-if="confirmPasswordError">
                  {{ confirmPasswordError }}
                </p>
              </div>
            </div>
            <div class="field is-grouped is-grouped-right">
              <div class="control">
                <button
                  class="button is-medium is-primary is-rounded"
                  type="button"
                  name="button"
                  @click="submitAccountDetails"
                >
                  Continue to MFA Setup
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Step 3: MFA Verification -->
        <div v-if="currentStep === 3">
          <cw-mfa-verification
            v-model="mfaCode"
            :phoneNumber="mfaPhoneNumber"
            :errorMessage="mfaErrorMessage"
            :showCancel="false"
            @verify="verifyMFACode"
            @resend="resendMFACode"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import jwt from 'jsonwebtoken';
import axios from 'axios';
let apiHost = process.env.VUE_APP_API_HOST;

import { validationMixin } from 'vuelidate';
import mfaEnrollment from '@/components/mixins/mfa-enrollment';
import {
  required,
  sameAs,
  minLength,
  maxLength,
} from 'vuelidate/lib/validators';

// Formatting during data entry
import Cleave from 'vue-cleave';
// Import us phone formatting
import 'cleave.js/dist/addons/cleave-phone.us';

import StepFlow from '@/components/cwkit/StepFlow';
import MFAVerification from '@/components/cwkit/MFAVerification';

export default {
  name: 'cw-accept-account',
  // share common functionality with component mixins
  mixins: [validationMixin, mfaEnrollment],
  components: {
    Cleave,
    'cw-step-flow': StepFlow,
    'cw-mfa-verification': MFAVerification,
  },
  props: {},
  // variables
  data() {
    return {
      currentStep: 2, // Start at step 2 since Registration (step 1) is already complete
      steps: [
        { id: 1, label: 'Registration' },
        { id: 2, label: 'Account Details' },
        { id: 3, label: 'MFA Verification' },
      ],
      firstName: '',
      lastName: '',
      phone: '',
      password: '',
      confirmPassword: '',
      mfaCode: '',
    };
  },
  validations: {
    firstName: {
      required,
      minLength: minLength(3),
    },
    lastName: {
      required,
      minLength: minLength(3),
    },
    phone: {
      required,
      // Include spaces in min and max length
      minLength: minLength(12),
      maxLength: maxLength(12),
    },
    password: {
      required,
      minLength: minLength(6),
    },
    confirmPassword: {
      required,
      sameAsPassword: sameAs('password'),
    },
  },
  computed: {
    tokenPayload() {
      let token = this.$route.query.token;
      let payload = jwt.decode(token);
      return payload;
    },
    passwordError() {
      let requiredErr = this.$v.password.$dirty && !this.$v.password.required;
      let lengthErr = this.$v.password.$dirty && !this.$v.password.minLength;

      if (requiredErr) {
        return 'A password is required.';
      } else if (lengthErr) {
        return 'Must be at least 6 characters.';
      } else {
        return null;
      }
    },
    confirmPasswordError() {
      let requiredErr =
        this.$v.confirmPassword.$dirty && !this.$v.confirmPassword.required;
      let matchErr =
        this.$v.confirmPassword.$dirty &&
        !this.$v.confirmPassword.sameAsPassword;

      if (requiredErr) {
        return 'Password confirmation is required.';
      } else if (matchErr) {
        return 'Passwords do not match.';
      } else {
        return null;
      }
    },
  },
  // methods
  watch: {},
  methods: {
    async submitAccountDetails() {
      // Validate Client side
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$modal.show('alert', {
          title: 'Insufficient Info',
          message:
            'Some of the fields are invalid. Please check that you have entered the required information.',
          type: 'warning',
        });
        return;
      }

      try {
        this.$modal.show('wifi-loader', { message: 'Creating account...' });

        await axios.post(`${apiHost}/auth/accept`, {
          token: this.$route.query.token,
          phone: this.phone,
          password: this.password,
          firstName: this.firstName,
          lastName: this.lastName,
        });
      } catch (err) {
        this.$modal.hide('wifi-loader');
        let res = err.response;
        if (res && res.data) {
          this.$modal.show('alert', {
            title: res.data.title,
            message: res.data.message,
            type: 'warning',
          });
          return;
        }
      }

      try {
        await this.$store.dispatch('user/authenticate', {
          username: this.tokenPayload.email,
          password: this.password,
        });
      } catch (err) {
        window.console.error(err);
        this.$modal.hide('wifi-loader');
        return;
      }

      // Get authenticated user's phone number from Firebase
      // This is already in E.164 format from the backend
      const user = this.$store.state.user;
      const phoneNumber = user.info && user.info.phoneNumber;

      if (!phoneNumber) {
        window.console.error('No phone number found for authenticated user');
        this.$modal.hide('wifi-loader');
        // Fall back to showing account created message
        this.$modal.show('alert', {
          title: 'Account Created',
          message:
            'Your account has been successfully created. You can enable two-factor authentication later in your account settings.',
          type: 'success',
        });
        setTimeout(() => {
          this.$router.push('/');
        }, 1000);
        return;
      }

      // Start MFA enrollment (from mixin)
      try {
        const result = await this.$store.dispatch('user/enrollMFA', {
          phoneNumber,
        });

        this.mfaVerificationId = result.verificationId;
        this.mfaPhoneNumber = phoneNumber;
        this.$modal.hide('wifi-loader');

        // Advance to MFA verification step
        this.currentStep = 3;
      } catch (error) {
        window.console.error('Error starting MFA enrollment:', error);
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Enrollment Error',
          message:
            error.message ||
            'Failed to start MFA enrollment. You can enable it later in your account settings.',
          buttons: {
            closeTitle: 'Ok',
          },
          type: 'warning',
        });

        // Allow user to continue without MFA
        setTimeout(() => {
          this.$router.push('/');
        }, 1500);
      }
    },
    async verifyMFACode(code) {
      // Handle both direct code parameter (from component) and mfaCode data property
      const verificationCode = typeof code === 'string' ? code : this.mfaCode;

      if (verificationCode.length !== 6) {
        return;
      }

      try {
        this.$modal.show('wifi-loader', { message: 'Verifying code...' });

        await this.$store.dispatch('user/verifyMFAEnrollment', {
          verificationId: this.mfaVerificationId,
          code: verificationCode,
          displayName: 'Phone',
        });

        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Account Setup Complete',
          message:
            'Your account has been successfully created and two-factor authentication is enabled.',
          type: 'success',
        });

        setTimeout(() => {
          this.$router.push('/');
        }, 1500);
      } catch (error) {
        window.console.error('Error verifying MFA code:', error);
        this.$modal.hide('wifi-loader');
        this.mfaErrorMessage = 'Invalid code. Please try again.';
      }
    },
    async resendMFACode() {
      try {
        // Clear any previous error
        this.mfaErrorMessage = '';
        this.$modal.show('wifi-loader', { message: 'Resending code...' });

        // Request new verification code
        const result = await this.$store.dispatch('user/enrollMFA', {
          phoneNumber: this.mfaPhoneNumber,
        });

        this.mfaVerificationId = result.verificationId;
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Code Resent',
          message: `A new verification code has been sent to ${this.formatPhoneForDisplay(
            this.mfaPhoneNumber
          )}.`,
        });
      } catch (error) {
        window.console.error('Error resending MFA code:', error);
        this.$modal.hide('wifi-loader');

        this.$modal.show('alert', {
          title: 'Error',
          message:
            'Failed to resend verification code. Please try again or restart the enrollment process.',
          type: 'warning',
        });
      }
    },
  },
};
</script>

<style lang="stylus" scoped></style>
